import React from 'react';
import axios from 'axios';
import {when} from 'mobx';
import {is_ad_user, is_user_ready, permission_list, normalize_permission, vms_list} from '../../userData.js';
import {LoadingScreen} from '../LoadingScreen.js';
import {active_perm} from '../../activeVM.js';
import {vm_cache} from '../util/WebconnectFrame.js';
import {poll_task} from '../../connectUtils.js';
import {logEvent} from '../../log';
import {newId, requestIdOf} from '../../log/correlation';


// Simple higher-order component that replaces the src property with the result
// of the async get_link() function defined below.

const component = Component => (

  class extends React.Component {

    constructor(props) {

      super(props);

      this.state = {src: ''};


      // React says you need to cancel this if the component gets unmounted.
      // I will just leave as-is because I think that's silly.

      // get_link reports its own failures (connection.launch.failed).
      (async () => this.setState({src: await get_link(props.perm_id)}))().catch(() => {});

    }

    render() {

      const {src, ...rest} = this.props;

      return (
        this.state.src ?
          <Component src={this.state.src} {...rest}/> :
          <LoadingScreen/>
      );
    }

  }

);
export default component


// Does an api call to derive the desired iframe src url from the vm_user_string

export const get_link = async perm_id => {

  let url, task_id;
  // One id for every request of this launch, so Tokyo's lines for it can be found together.
  const operation_id = newId();
  const headers = {'X-Operation-ID': operation_id};
  let step = 'request';

  try {

    // Wait for /api/users/ to resolve so is_ad_user is known before we pick
    // the endpoint. Without this, direct-perm loads call the wrong API because
    // is_ad_user is still undefined when TransformVMLink first mounts.
    if (!is_user_ready.get()) {
      await when(() => is_user_ready.get());
    }

    // AD non-pool: permission_list must be populated to know perm.type and perm.id.
    // If not ready yet, bail — the loading screen will remount Body with full data
    // and a fresh TransformVMLink will retry with everything available.
    if (is_ad_user.get() && !perm_id.startsWith('pool') && !permission_list.get()[perm_id]) {
      return '';
    }

    if(is_ad_user.get() && perm_id.startsWith('pool')) {

      // https://app.v2cloud.com/#/pool_70
      // https://app.v2cloud.com/#/pool_70_82

      // eslint-disable-next-line
      const [_, pool_id, access_id] = perm_id.split('_')

      let q = `/api/advmpoolaccesss/${pool_id}/webconnect_direct_url?format=json`;

      // eslint-disable-next-line
      const data = (await axios({
        url: q,
        headers,
        params: {
          ...(access_id && { access: access_id }),
          format: 'json'
        }
      })).data;

      url = data.url;

    } else if(is_ad_user.get() && !perm_id.startsWith('pool')) {

      let pl = permission_list.get();
      let perm = pl[perm_id];

      let q = ''

      if(perm.type === 'vmpool' || perm.type === 'vmpool-app') {
        q = `/api/advmpoolaccesss/${perm.id}/webconnect_direct_url`;
      } else {
        q = `/api/advmaccesss/${perm.id}/webconnect_direct_url`;
      }

      const data = (await axios({
        url: q,
        headers,
        params: {
          ...(perm.app && { app: perm.app.id }),
          format: 'json'
        }
      })).data;

      // these checks can be removed onced format=json is supported in stable
      if(typeof data === 'string') {
        url = data;
      } else {
        url = data.url;
        task_id = data.task && data.task.id;
        pl[perm_id].last_vmpool_vm_id = data.vm;
        pl[perm_id].last_vmpool_vmuser_id = data.vmuser;
        permission_list.set(pl);
      }

    } else {

      // Direct hash links can resolve before load_permissions_and_vms()
      // finishes in the background. BareFrame reads permission_list once on
      // mount to decide iframe camera access and never recomputes it, so a
      // still-empty permission_list here means no camera for the rest of the
      // session. Backfill this one permission alongside the connect call.
      const needs_backfill = !permission_list.get()[perm_id];

      const [connectRes, permRes] = await Promise.all([
        axios({
          url: `/api/permissions/${perm_id}/webconnect_direct_url`,
          headers,
          params: {
            format: 'json'
          }
        }),
        needs_backfill
          ? axios({url: `/api/permissions/${perm_id}/`, headers, params: {format: 'json'}}).catch(() => null)
          : Promise.resolve(null)
      ]);

      const data = connectRes.data;

      if(typeof data === 'string') {
        url = data;
      } else {

        url = data.url;
        task_id = data.task && data.task.id;

        const pl = permission_list.get();

        // Recheck pl[perm_id] instead of trusting needs_backfill — the
        // background load may have won the race while these requests were
        // in flight, and we don't want to clobber it.
        if(needs_backfill && permRes && !pl[perm_id]) {

          const perm = normalize_permission(permRes.data);
          pl[perm_id] = perm;

          // Same vm_out bookkeeping load_permissions() does, so vms_list
          // doesn't fall out of sync with permission_list.
          const vl = vms_list.get();
          if(perm.vm && perm.vm.id) vl[perm.vm.id] = perm.vm;
          if(perm.pool_vm && perm.pool_vm.id) vl[perm.pool_vm.id] = perm.pool_vm;
          // New reference — mobx won't notify observers on the same one.
          vms_list.set({...vl});

        }

        // Reaches here for the already-populated case too, to persist
        // last_vmpool_vm_id/vmuser_id below.
        if(pl[perm_id]) {
          pl[perm_id].last_vmpool_vm_id = data.vm;
          pl[perm_id].last_vmpool_vmuser_id = data.vmuser;
          permission_list.set({...pl});
        }

        if(task_id) {
          step = 'task';
          await poll_task(task_id, headers);
        }
      }
    }

    if(!url || typeof url !== 'string') {
      step = 'url';
      throw Object.assign(new Error('The launch answer has no connection URL'), {code: 'no_url'});
    }

  } catch(e) {

    logEvent('connection.launch.failed', {
      connection_type: 'webconnect',
      step,
      perm_id: /^\d+$/.test(perm_id) ? Number(perm_id) : undefined,
      status: e.response && e.response.status,
      error_code: e.code || (e.response ? undefined : 'network'),
      request_id: requestIdOf(e),
      operation_id,
    });
    active_perm.set();
    vm_cache.delete(perm_id);

    throw e;

  }


  if(window.location.hostname === '127.0.0.1') {

    // go through our proxy so that we have same-origin conditions


    const without_protocol = url.substring('https://'.length);

    const without_path = without_protocol.split('/')[0];

    const subdomain = without_path.split('.').slice(0, -2).join('.')


    let without_orginSlash = without_protocol.split('/');
    without_orginSlash.shift();
    without_orginSlash = without_orginSlash.join('/');


    // see electron/server.js

    url = window.location.origin + '/subdomain/' + subdomain + '/' + without_orginSlash;

  }


  // Tell webconnect it is embedded in our client, which hides the task
  // bar and leaves logout handling to us. v2desktopclient is the real
  // signal. v2mobileclient stays for now, older webconnect extensions
  // only suppress the logout redirect for the mobile param. Drop it
  // once the proxy fleet is updated.
  url += '&v2desktopclient&v2mobileclient';

  return url;

};
